import { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'lucide-react';

/**
 * BaseModal — standard modal primitive for the entire app.
 *
 * Built on @headlessui/react Dialog for automatic:
 * - Focus trapping
 * - ESC to close
 * - Backdrop click to close
 * - ARIA dialog role & attributes
 * - Scroll locking
 *
 * Usage:
 *   <BaseModal isOpen={open} onClose={setOpen} title="Confirm">
 *     <BaseModal.Body>Content here</BaseModal.Body>
 *     <BaseModal.Footer>
 *       <button onClick={() => setOpen(false)}>Cancel</button>
 *       <button onClick={handleSave}>Save</button>
 *     </BaseModal.Footer>
 *   </BaseModal>
 *
 * Sizes: sm (max-w-sm), md (max-w-lg), lg (max-w-2xl), xl (max-w-4xl), full (max-w-5xl)
 */

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-5xl',
};

export default function BaseModal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  showClose = true,
  children,
}) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-modal">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        </TransitionChild>

        {/* Panel */}
        <div className="flex fixed inset-0 justify-center items-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel
              className={`flex flex-col w-full ${SIZE_CLASSES[size]} max-h-[85vh] bg-white rounded-xl shadow-xl`}
            >
              {/* Header */}
              {(title || showClose) && (
                <div className="flex justify-between items-start px-6 py-4 border-b border-gray-200">
                  <div className="min-w-0">
                    {title && (
                      <DialogTitle className="text-lg font-semibold text-gray-900">
                        {title}
                      </DialogTitle>
                    )}
                    {subtitle && (
                      <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
                    )}
                  </div>
                  {showClose && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-shrink-0 p-2 -m-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              )}

              {/* Children (Body + Footer) */}
              {children}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

/** Scrollable body section inside BaseModal. */
BaseModal.Body = function ModalBody({ children, className = '' }) {
  return (
    <div className={`overflow-y-auto flex-1 px-6 py-4 min-h-0 ${className}`}>
      {children}
    </div>
  );
};

/** Fixed footer with consistent button layout. */
BaseModal.Footer = function ModalFooter({ children, className = '' }) {
  return (
    <div className={`flex gap-3 justify-end px-6 py-4 border-t border-gray-200 ${className}`}>
      {children}
    </div>
  );
};
